/*
 * Moving an environment, or a whole site, to another server (docs/moving.md part 2; docs/api.md, "Server moves").
 * Only Hosterr's team starts and steers a move (lib/roles.ts canMoveEnvironments, with the API's `canMove` as the
 * exact answer); anyone who can see the environment sees its timeline, without server names.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useOrg } from "../app/org";
import { api } from "./client";
import { keys } from "./keys";
import { onOperationEvent } from "./live";

export type MoveState = "queued" | "preparing" | "copying" | "catching_up" | "certificate" | "ready" | "switching" | "waiting_dns" | "serving" | "cleaning" | "canceling" | "done" | "failed" | "canceled";
export type MoveStepState = "done" | "running" | "waiting" | "failed" | "skipped";

export type MoveCheck = { id: string; ok: boolean | null; text: string };
export type MoveDnsRecord = { name: string; type: "A" | "AAAA"; from: string; to: string | null; state: "pending" | "lowered" | "switched" | "failed"; error: string | null };
export type MoveDnsExternal = { name: string; type: "A" | "AAAA"; value: string; zone: string | null; relative: string; provider: string | null; seen: { at: string; pointsHere: boolean; detail: string } | null };
export type MoveDns = { hosterr: MoveDnsRecord[]; external: MoveDnsExternal[]; checkedAt: string | null; note: string | null };

export type MovePlan = {
  ok: boolean;
  checks: MoveCheck[];
  source: { id: string; name: string; ipv4: string | null };
  target: { id: string; name: string; ipv4: string | null; ipv6: string | null };
  sizes: { filesBytes: number | null; dbBytes: number | null };
  copyMs: number | null;
  freezeMs: number | null;
  switchWhen: "ready" | "quiet";
  quietWindow: string | null;
  dns: MoveDns;
};

export type MoveTarget = { id: string; name: string; ipv4: string | null; region: string | null; ok: boolean; reason: string | null };

export type Move = {
  id: string;
  environmentId: string;
  siteId: string;
  environment: { id: string; name: string; slug: string; label: string };
  state: MoveState;
  detail: string | null;
  /** Null for people who don't manage servers. */
  source: { id: string; name: string; ipv4: string | null } | null;
  target: { id: string; name: string; ipv4: string | null; ipv6: string | null } | null;
  timeline: { id: "prepare" | "copy" | "catchup" | "certificate" | "switch" | "dns" | "finish"; label: string; state: MoveStepState; detail: string | null }[];
  rounds: { n: number; kind: "copy" | "catchup" | "final"; startedAt: string; finishedAt: string | null; ms: number | null; files: { bytes: number; files: number; ms: number } | null; db: { bytes: number; tables: number; ms: number } | null }[];
  /** The running copy's last progress line from the agent. */
  live: { part: "files" | "db"; percent: number | null; bytes: number | null; rate: string | null; files: number | null; at: string } | null;
  sizes: { filesBytes: number | null; dbBytes: number | null };
  expectedCopyMs: number | null;
  freezeEstimateMs: number | null;
  freezeMs: number | null;
  switchAt: string | null;
  switchNow: boolean;
  switchWhen: string;
  certificate: { names: string[] };
  dns: MoveDns | null;
  error: { message: string; step: string | null; at: string | null } | null;
  operationId: string | null;
  finalBackup: { id: string | null; storage: boolean } | null;
  createdBy: { id: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
  switchedAt: string | null;
  dnsMovedAt: string | null;
  finishedAt: string | null;
  actions: { switchNow: boolean; cancel: boolean; finish: boolean; retry: boolean; checkDns: boolean };
};

export const OPEN_STATES: MoveState[] = ["queued", "preparing", "copying", "catching_up", "certificate", "ready", "switching", "waiting_dns", "serving", "cleaning", "canceling", "failed"];
/** States in which something runs on a server right now. */
export const BUSY_STATES: MoveState[] = ["preparing", "copying", "catching_up", "certificate", "switching", "cleaning", "canceling"];

const moveKey = (orgId: string, siteId: string, envId: string) => [...keys.site(orgId, siteId), "move", envId] as const;
const orgMovesKey = (orgId: string) => [...keys.org(orgId), "environment-moves"] as const;
const base = (orgId: string, siteId: string, envId: string) => `/orgs/${orgId}/sites/${siteId}/environments/${envId}/move`;

/** Refetches `key` whenever one of the site's operations finishes (each step of a move is one). */
function useRefetchOnOps(key: readonly unknown[], siteId: string | null) {
  const qc = useQueryClient();
  const k = JSON.stringify(key);
  useEffect(() => {
    const off = onOperationEvent((op) => {
      if (!siteId || op.siteId !== siteId) return;
      void qc.invalidateQueries({ queryKey: JSON.parse(k) });
    });
    return () => {
      off();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc, k, siteId]);
}

/** The environment's open move (or the last one, for a day), and whether moving it is offered to this person. */
export function useEnvMove(siteId: string, envId: string | null | undefined) {
  const org = useOrg();
  const key = moveKey(org.id, siteId, envId ?? "");
  useRefetchOnOps(key, siteId);
  return useQuery({
    queryKey: key,
    queryFn: ({ signal }) => api.get<{ move: Move | null; canMove: boolean }>(base(org.id, siteId, envId!), signal),
    enabled: !!envId,
    // While something runs, a few seconds apart, so the copy's progress moves.
    refetchInterval: (q) => (q.state.data?.move && BUSY_STATES.includes(q.state.data.move.state) ? 3_000 : q.state.data?.move && OPEN_STATES.includes(q.state.data.move.state) ? 30_000 : false),
  });
}

/** The servers it could go to: the environment's (or, without one, every environment's), each with why not. */
export function useMoveTargets(siteId: string, envId: string | null, enabled: boolean) {
  const org = useOrg();
  return useQuery({
    queryKey: [...keys.site(org.id, siteId), "move-targets", envId ?? "all"],
    queryFn: ({ signal }) => api.get<{ targets: MoveTarget[] }>(`/orgs/${org.id}/sites/${siteId}/move/targets${envId ? `?environmentId=${envId}` : ""}`, signal),
    select: (d) => d.targets,
    enabled,
    staleTime: 0,
  });
}

/** The plan for one environment, or every environment (envId null), onto one server. Changes nothing. */
export function useMovePlan(siteId: string, envId: string | null, serverId: string | null) {
  const org = useOrg();
  return useQuery({
    queryKey: [...keys.site(org.id, siteId), "move-plan", envId ?? "all", serverId ?? ""],
    queryFn: async () =>
      envId
        ? [{ environment: null, plan: (await api.post<{ plan: MovePlan }>(`${base(org.id, siteId, envId)}/plan`, { serverId })).plan }]
        : (await api.post<{ plans: { environment: { id: string; name: string; slug: string; label: string }; plan: MovePlan }[] }>(`/orgs/${org.id}/sites/${siteId}/move/plan`, { serverId })).plans,
    enabled: !!serverId,
    placeholderData: keepPreviousData,
    staleTime: 0,
    retry: false,
  });
}

function useRefresh(siteId: string) {
  const qc = useQueryClient();
  const org = useOrg();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.site(org.id, siteId) });
    void qc.invalidateQueries({ queryKey: orgMovesKey(org.id) });
  };
}

/** Starts a move of one environment, or of every environment of the site (envId null). */
export function useStartMove(siteId: string, envId: string | null) {
  const org = useOrg();
  const refresh = useRefresh(siteId);
  return useMutation({
    mutationFn: async (serverId: string) => (envId ? [(await api.post<{ move: Move }>(base(org.id, siteId, envId), { serverId })).move] : (await api.post<{ moves: Move[] }>(`/orgs/${org.id}/sites/${siteId}/move`, { serverId })).moves),
    onSuccess: refresh,
  });
}

export type MoveAction = "switch" | "cancel" | "finish" | "retry" | "dns-check";

export function useMoveAction(siteId: string, envId: string) {
  const org = useOrg();
  const qc = useQueryClient();
  const refresh = useRefresh(siteId);
  return useMutation({
    mutationFn: (a: MoveAction) => api.post<{ move: Move }>(`${base(org.id, siteId, envId)}/${a}`, a === "finish" ? { confirm: true } : {}),
    onSuccess: (r) => {
      qc.setQueryData<{ move: Move | null; canMove: boolean }>(moveKey(org.id, siteId, envId), (old) => ({ canMove: old?.canMove ?? false, move: r.move }));
      refresh();
    },
  });
}

/** The org's moves, open or ended in the last ten minutes: the task tray. */
export function useOrgMoves() {
  const org = useOrg();
  return useQuery({
    queryKey: orgMovesKey(org.id),
    queryFn: ({ signal }) => api.get<{ moves: Move[] }>(`/orgs/${org.id}/environment-moves`, signal),
    select: (d) => d.moves,
    refetchInterval: (q) => (q.state.data?.moves.some((m) => BUSY_STATES.includes(m.state)) ? 5_000 : q.state.data?.moves.some((m) => OPEN_STATES.includes(m.state)) ? 60_000 : false),
  });
}

/** "about 20 seconds", "about 3 minutes", as the API says them. */
export function durationWords(ms: number): string {
  if (ms < 1000) return "under a second";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} ${s === 1 ? "second" : "seconds"}`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} ${m === 1 ? "minute" : "minutes"}`;
  const h = Math.round((m / 60) * 10) / 10;
  return `${h} ${h === 1 ? "hour" : "hours"}`;
}
